import { motion } from "motion/react";
import { EASE, SPRING } from "@/components/motion/motion";
import { usePlayOnce } from "@/hooks/use-play-once";
import {
	breakEvenYear,
	COSTS,
	fillTemplate,
	formatEuro,
	type SaasPlan,
	saasYearly,
} from "@/lib/costs";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

const YEARS = 6;
const STEP = 20_000;
const DRAW = { duration: 1.8, ease: EASE, delay: 0.2 } as const;
const INSTANT = { duration: 0 } as const;

const { build, yearly } = COSTS.businessApp;
const customAt = (year: number) => build.after + yearly.after * year;

/* Plot in a 600×300 box stretched to the container; strokes don't scale. */
const x = (year: number) => (year / YEARS) * 600;
const percentX = (year: number) => `${(year / YEARS) * 100}%`;

/** Scales and paths of the chart for one subscription hypothesis. */
const buildChart = (plan: SaasPlan) => {
	const perYear = saasYearly(plan);
	const saasAt = (year: number) => perYear * year;
	const max = Math.ceil(Math.max(saasAt(YEARS), customAt(YEARS)) / STEP) * STEP;
	const y = (value: number) => 300 - (value / max) * 300;
	return {
		perYear,
		max,
		breakEven: breakEvenYear(plan),
		saasPath: `M0 ${y(0)} L${x(YEARS)} ${y(saasAt(YEARS))}`,
		customPath: `M0 ${y(0)} L0 ${y(build.after)} L${x(YEARS)} ${y(customAt(YEARS))}`,
		percentY: (value: number) => `${(1 - value / max) * 100}%`,
	};
};

/**
 * Cumulative cost of a SaaS subscription vs a custom build over six years:
 * the custom line starts higher (the build), grows slower, and the two meet
 * at the break-even year. Server and reduced motion render the drawn chart.
 */
export function CostCurves({
	saas = COSTS.saas,
	title,
	subscriptionName,
	className,
}: {
	/** Subscription compared with the custom build (site default otherwise). */
	saas?: SaasPlan;
	title?: string;
	subscriptionName?: string;
	className?: string;
}) {
	const { dict, locale } = useI18n();
	const copy = dict.economics.saas;
	const { ref, rewound } = usePlayOnce<HTMLElement>();
	const euro = (value: number) => formatEuro(value, locale);
	const chart = buildChart(saas);
	const { max, breakEven, percentY } = chart;
	const breakEvenCost = customAt(breakEven);

	const lines = [
		{
			key: "subscription",
			name: subscriptionName ?? copy.subscription.name,
			detail: fillTemplate(copy.subscription.detail, {
				users: saas.users,
				price: euro(saas.monthlyPerUser),
			}),
			swatch: "bg-foreground/35",
		},
		{
			key: "custom",
			name: copy.custom.name,
			detail: fillTemplate(copy.custom.detail, {
				build: euro(build.after),
				yearly: euro(yearly.after),
			}),
			swatch: "bg-signal",
		},
	];

	return (
		<figure
			ref={ref}
			className={cn("rounded-3xl border bg-card p-6 md:p-8", className)}
		>
			<figcaption className="font-mono text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
				{title ?? copy.title}
			</figcaption>
			<p className="sr-only">
				{fillTemplate(copy.summary, {
					years: breakEven,
					total: euro(breakEvenCost),
					saas: euro(chart.perYear),
					custom: euro(yearly.after),
				})}
			</p>

			<div aria-hidden="true" className="mt-8 pb-8 pl-16 sm:pl-[4.5rem]">
				<div className="relative h-52 sm:h-64">
					{Array.from(
						{ length: max / STEP },
						(_, index) => (index + 1) * STEP,
					).map((value) => (
						<div
							key={value}
							className="absolute inset-x-0 border-t border-dashed"
							style={{ top: percentY(value) }}
						>
							<span className="absolute right-full mr-3 -translate-y-1/2 font-mono text-[10px] whitespace-nowrap text-muted-foreground tabular-nums">
								{euro(value)}
							</span>
						</div>
					))}
					<div className="absolute inset-x-0 bottom-0 border-t border-foreground/25" />
					<div className="absolute inset-x-0 top-full mt-2">
						{Array.from({ length: YEARS + 1 }, (_, year) => (
							<span
								key={year}
								className="absolute -translate-x-1/2 font-mono text-[10px] whitespace-nowrap text-muted-foreground tabular-nums"
								style={{ left: percentX(year) }}
							>
								{year}
							</span>
						))}
					</div>

					<svg
						aria-hidden="true"
						viewBox="0 0 600 300"
						preserveAspectRatio="none"
						className="absolute inset-0 size-full overflow-visible"
					>
						<motion.path
							d={chart.saasPath}
							fill="none"
							className="stroke-foreground/35"
							strokeWidth={2}
							strokeLinecap="round"
							vectorEffect="non-scaling-stroke"
							initial={false}
							animate={{ pathLength: rewound ? 0 : 1 }}
							transition={rewound ? INSTANT : DRAW}
						/>
						<motion.path
							d={chart.customPath}
							fill="none"
							className="stroke-signal"
							strokeWidth={3}
							strokeLinecap="round"
							strokeLinejoin="round"
							vectorEffect="non-scaling-stroke"
							initial={false}
							animate={{ pathLength: rewound ? 0 : 1 }}
							transition={rewound ? INSTANT : DRAW}
						/>
					</svg>

					<motion.div
						className="absolute"
						style={{
							left: percentX(breakEven),
							top: percentY(breakEvenCost),
						}}
						initial={false}
						animate={{ opacity: rewound ? 0 : 1 }}
						transition={
							rewound
								? INSTANT
								: {
										duration: 0.4,
										ease: EASE,
										delay: DRAW.delay + DRAW.duration,
									}
						}
					>
						<motion.span
							className="absolute size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-card bg-signal ring-4 ring-signal/20"
							initial={false}
							animate={{ scale: rewound ? 0 : 1 }}
							transition={
								rewound
									? INSTANT
									: { ...SPRING, delay: DRAW.delay + DRAW.duration }
							}
						/>
						<span className="absolute right-4 bottom-4 rounded-full border bg-background px-2.5 py-1 font-mono text-[10px] font-medium whitespace-nowrap">
							{fillTemplate(copy.crossing, { years: breakEven })}
						</span>
					</motion.div>
				</div>
			</div>

			<ul className="mt-4 grid gap-4 border-t pt-5 sm:grid-cols-2">
				{lines.map((line) => (
					<li key={line.key} className="flex gap-3">
						<span
							aria-hidden="true"
							className={cn("mt-2 h-1 w-5 shrink-0 rounded-full", line.swatch)}
						/>
						<span>
							<span className="block text-sm font-semibold">{line.name}</span>
							<span className="block text-sm text-pretty text-muted-foreground">
								{line.detail}
							</span>
						</span>
					</li>
				))}
			</ul>
		</figure>
	);
}
